import {cn} from '@rocket-ui/tools';
import React, {FC, PropsWithChildren} from 'react';
import {Icon} from '../../Icon';
import {ButtonIcon, IconType} from './ButtonIcon';
import {sizeClasses, variantClasses} from './config';
import {ButtonSize, ButtonVariant} from './types';

interface Props {
	variant?: ButtonVariant;
	leftIcon?: IconType;
	rightIcon?: IconType;
	size?: ButtonSize;
	round?: boolean;
	loading?: boolean;
	isEllipsis?: boolean;
}

type ButtonProps = PropsWithChildren<Props & React.ButtonHTMLAttributes<HTMLButtonElement>>;

export const Button: FC<ButtonProps> = ({
	variant = 'tonal',
	leftIcon,
	rightIcon,
	size = 'md',
	loading,
	disabled,
	children,
	round,
	className,
	isEllipsis,
	...props
}) => {
	const sizeConfig = sizeClasses[size];

	return (
		<button
			className={cn(
				'relative group flex-1 grid place-content-center font-medium leading-tight line-clamp-1 rounded-md',
				sizeConfig.height,
				sizeConfig.px,
				variantClasses[variant],
				{
					'rounded-full': round,
					'transition active:scale-95': !disabled,
				},
				className,
			)}
			role={'button'}
			{...props}
			disabled={disabled || loading}
		>
			<div className={cn('flex items-center justify-center', {invisible: loading})}>
				{leftIcon ? (
					<span className={cn('grid place-content-center', sizeConfig.icon.padding)}>
						<ButtonIcon icon={leftIcon} className={sizeConfig.icon.sizes} />
					</span>
				) : null}
				<span
					className={cn(sizeConfig.compensation, {
						'overflow-hidden overflow-ellipsis line-clamp-2': isEllipsis,
					})}
				>
					{children}
				</span>
				{rightIcon ? (
					<span className={cn('grid place-content-center', sizeConfig.icon.padding)}>
						<ButtonIcon icon={rightIcon} className={sizeConfig.icon.sizes} />
					</span>
				) : null}
			</div>

			{loading && (
				<div className={'absolute inset-0 grid place-content-center'}>
					<Icon name={'loading'} />
				</div>
			)}
		</button>
	);
};
